import { ReactElement } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useFlags } from '@flagsmith/flagsmith/react';
import { Alert } from './Alert';
import { MenuButton } from './MenuButton';
import { ProductRating } from '../ReviewsContent/ProductRating';
import { BreakpointMedia } from '../../../utils/config';
import { Product, Sku } from '../../../models/product.types';
import { useMenuWithCart } from '../../../contexts/MenuWithCart';
import { Breadcrumb } from '../Breadcrumb';
import { useNewProspectWelcomeToast } from '../../../hooks/useNewProspectWelcomeToast';
import styles from '../ProductDisplayPage.module.scss';
import { SubscriptionBenefits } from './SubscriptionBenefits';

type Props = {
  sku: Sku,
  product: Product,
}

export function ProductDetails({
  sku,
  product,
}: Props): ReactElement {
  const { cart } = useMenuWithCart();

  useNewProspectWelcomeToast(cart);

  const isGrocery = sku.product_type_name === 'Grocery';

  const flags = useFlags(['display-customer-reviews-pdp']);
  const customerReviewFlag = flags['display-customer-reviews-pdp'];
  const showCustomerReviewsPdp = customerReviewFlag.enabled && customerReviewFlag.value === 'show';

  const stackElements = useMediaQuery({ query: BreakpointMedia.MAX_MD });
  const isMobileWidth = useMediaQuery({ query: BreakpointMedia.MAX_SM });

  const nutrition = {
    Cal: Math.round(sku?.calories ?? 0),
    Fat: `${Math.round(Number(sku?.fat ?? 0))}g`,
    Carbs: `${Math.round(Number(sku?.carbs ?? 0))}g`,
    Protein: `${Math.round(Number(sku?.protein ?? 0))}g`,
    Fiber: `${Math.round(Number(sku?.fiber ?? 0))}g`,
  };
  const badgeTags = sku?.tags?.filter((tag) => {
    if (tag.name === 'Low Sodium') {
      tag.name = 'Sodium Smart';
    }
    return ['Gluten-Free', 'High-Protein', 'High Fiber', 'Sodium Smart'].includes(tag.name);
  });

  const productTypeName = sku.product_type_name.replaceAll(' ', '_') as 'Grocery' | 'Ready_to_Eat' | 'Meal_Kit';

  return (
    <div className={styles.details_layout_container}>
      <div className={styles.details_container}>
        {!stackElements && (
          <>
            <Breadcrumb pageTitle={product.title} />
            <Alert />
          </>
        )}

        {isGrocery && sku.brand && (
          <h2
            className={styles.product_subtitle}
            style={stackElements ? { margin: 0 } : { marginTop: '16px' }}
          >
            {sku.brand}
          </h2>
        )}

        <h1 className={styles.product_title}>{product.title}</h1>

        {product.subtitle && (
          <h2 className={styles.product_subtitle}>
            {product.subtitle.charAt(0)
              .toUpperCase() + product.subtitle.slice(1)}
          </h2>
        )}

        {showCustomerReviewsPdp && (
          <ProductRating
            productId={product.id}
            className={product.subtitle ? '' : styles.prospect_product_rating_pdp}
            size="md"
          />
        )}

        {!isGrocery && (
          <div
            className={styles.nutrition_row_container}
            style={{
              paddingTop: '11px',
              margin: 0,
            }}
          >
            {Object.entries(nutrition)
              .filter(([, value]) => value !== 0 && value !== '0g')
              .map(
                ([unit, value]) => (
                  <span key={unit} className={styles.nutrient}>
                    <span>
                      <span className={styles.nutrient_value}>{value}</span>
                      {' '}
                      <span className={styles.nutrient_unit}>{unit}</span>
                    </span>
                    <span className={styles.separator}>•</span>
                  </span>
                ),
              )}

            <div className={styles.nutrient}>
              <span className={styles.nutrient_value}>
                {`${sku.prep_and_cook_time} min `}
              </span>
              <span
                className={styles.nutrient_unit}
                style={{ textTransform: 'none' }}
              >
                Cook Time
              </span>
              <span className={styles.separator}>•</span>
              <span className={styles.nutrient_value}>{sku.servings}</span>
              <span
                className={styles.nutrient_unit}
                style={{ textTransform: 'none' }}
              >
                {sku.servings === 1 ? ' Serving' : ' Servings'}
              </span>
            </div>
          </div>
        )}

        {badgeTags.length > 0 && (
          <div className={styles.badge_tags_container} style={{ margin: '10px 0 0' }}>
            {badgeTags.map((tag) => (
              <div className={styles.badge_tag} key={tag.id}>
                {tag.name}
              </div>
            ))}
          </div>
        )}

        <hr className={styles.prospect_detail_divider} />

        {!isMobileWidth && (
          <MenuButton productType={productTypeName} />
        )}

        <div className={styles.prospect_subscription_benefits}>
          <SubscriptionBenefits />
        </div>
      </div>

      {(isMobileWidth) && (
        <div
          className={styles.sticky_footer}
          style={{
            textAlign: 'left',
            padding: '5px',
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          >
            <MenuButton productType={productTypeName} isMobileWidth />
          </div>
        </div>
      )}
    </div>
  );
}
